import { Action, Dispatch, Reducer } from 'redux';
import { PageResult } from '../../models/CoreModels';
import { ScheduleSync_CoreEventRef, ScheduleSync_CoreTrackings, ScheduleSync_CoreUser, ScheduleSync_CoreUserListing, ScheduleSync_UsersFilter, ScheduleSync_UserScheduleFilter } from '../../models/CoreScheduleSyncModels';
import { CacheRequestState, ExtendedRequestState, RequestState } from '../../models/RequestState';
import InstanceClient from '../../services/InstanceClient';
import StatusCode from '../../util/StatusCode';
import store from '../store';

enum ScheduleSync_UserActionType {
    SCHEDULESYNC_USERS = 'SCHEDULESYNC_USERS',
    SCHEDULESYNC_USERS_FILTER = 'SCHEDULESYNC_USERS_FILTER',
    SCHEDULESYNC_USERS_UPDATE = 'SCHEDULESYNC_USERS_UPDATE',
    SCHEDULESYNC_USER = 'SCHEDULESYNC_USER',
    SCHEDULESYNC_USER_TRACKINGS = 'SCHEDULESYNC_USER_TRACKINGS',
    SCHEDULESYNC_USER_SCHEDULE = 'SCHEDULESYNC_USER_SCHEDULE',
}

export type UserEventsRequestState = ExtendedRequestState<ScheduleSync_CoreEventRef[], { filter: Partial<ScheduleSync_UserScheduleFilter> }>

export interface ScheduleSyncUserState {
    users: CacheRequestState<PageResult<ScheduleSync_CoreUserListing>>    
    filter: ScheduleSync_UsersFilter
    user: RequestState<ScheduleSync_CoreUser>
    userTrackings: RequestState<ScheduleSync_CoreTrackings>
    events: UserEventsRequestState
}

interface ScheduleSync_ListUsersAction extends Action<ScheduleSync_UserActionType> {
    type: ScheduleSync_UserActionType.SCHEDULESYNC_USERS
    users: CacheRequestState<PageResult<ScheduleSync_CoreUserListing>>
}

interface ScheduleSync_UsersFilterAction extends Action<ScheduleSync_UserActionType> {
    type: ScheduleSync_UserActionType.SCHEDULESYNC_USERS_FILTER
    filter: ScheduleSync_UsersFilter
}

interface ScheduleSync_UsersUpdateAction extends Action<ScheduleSync_UserActionType> {
    type: ScheduleSync_UserActionType.SCHEDULESYNC_USERS_UPDATE
}

interface ScheduleSync_GetUserAction extends Action<ScheduleSync_UserActionType> {
    type: ScheduleSync_UserActionType.SCHEDULESYNC_USER
    user: RequestState<ScheduleSync_CoreUser>
}

interface ScheduleSync_UserTrackingsAction extends Action<ScheduleSync_UserActionType> {
    type: ScheduleSync_UserActionType.SCHEDULESYNC_USER_TRACKINGS
    userTrackings: RequestState<ScheduleSync_CoreTrackings>
}

interface ScheduleSync_UserScheduleAction extends Action<ScheduleSync_UserActionType> {
    type: ScheduleSync_UserActionType.SCHEDULESYNC_USER_SCHEDULE
    events: UserEventsRequestState
}

type ScheduleSync_UserAction =
    | ScheduleSync_ListUsersAction
    | ScheduleSync_UsersFilterAction
    | ScheduleSync_UsersUpdateAction
    | ScheduleSync_GetUserAction
    | ScheduleSync_UserTrackingsAction
    | ScheduleSync_UserScheduleAction


export const ScheduleSyncUsers = (filter: Partial<ScheduleSync_UsersFilter> = {}, update: boolean = false) => (dispatch: Dispatch<ScheduleSync_ListUsersAction | ScheduleSync_UsersFilterAction | ScheduleSync_UsersUpdateAction>) => {
    dispatch({ type: ScheduleSync_UserActionType.SCHEDULESYNC_USERS_FILTER, filter: filter });
    if (update) {
        dispatch({ type: ScheduleSync_UserActionType.SCHEDULESYNC_USERS_UPDATE });
    }

    const url = '/api/schedulesync/v1/core/users'
    return InstanceClient.get<PageResult<ScheduleSync_CoreUserListing>>(url, filter)
        .then(res => {
            dispatch({
                type: ScheduleSync_UserActionType.SCHEDULESYNC_USERS,
                users: {
                    code: StatusCode.COMPLETE,
                    data: res.data                    
                }
            })
        })
        .catch(err =>
            dispatch({
                type: ScheduleSync_UserActionType.SCHEDULESYNC_USERS,
                users: { code: StatusCode.ERROR, error: err }
            })
        );
};

export const GetScheduleSyncUser = (id: string) => (dispatch: Dispatch<ScheduleSync_GetUserAction>) => {
    const storeKey = store.getState().schedulesync_users.user?.key;
    const key = id;
    if (key === storeKey) {
        return;
    }

    dispatch({
        type: ScheduleSync_UserActionType.SCHEDULESYNC_USER,
        user: { code: StatusCode.PENDING },
    });

    const url = `/api/schedulesync/v1/core/user/${id}`;
    return InstanceClient.get<ScheduleSync_CoreUser>(url)
        .then(res => {
            dispatch({
                type: ScheduleSync_UserActionType.SCHEDULESYNC_USER,
                user: {
                    code: StatusCode.COMPLETE,
                    data: res.data,
                    key: key,
                }
            })
        })
        .catch(error =>
            dispatch({
                type: ScheduleSync_UserActionType.SCHEDULESYNC_USER,
                user: { code: StatusCode.ERROR, error }
            })
        );
};


export const UserSchedule = (id: string, filter: Partial<ScheduleSync_UserScheduleFilter>) => (dispatch: Dispatch<ScheduleSync_UserScheduleAction>) => {
    const storeKey = store.getState().schedulesync_users.events?.key;
    const key = id + '-' + filter.onlyActive + '-' + filter.weekDate;
    if (key === storeKey) {
        return;
    }

    dispatch({
        type: ScheduleSync_UserActionType.SCHEDULESYNC_USER_SCHEDULE,
        events: {
            code: StatusCode.PENDING, filter: filter
        },
    });

    const queryParams: string[] = []
    if (filter.onlyActive) {
        queryParams.push(`onlyActive=${filter.onlyActive}`)
    }
    if (filter.weekDate) {
        queryParams.push(`weekDate=${filter.weekDate.toJSON()}`)
    }

    const fullQuery = queryParams.join('&')
    const url = '/api/schedulesync/v1/core/user/' + id + '/schedule?' + fullQuery;
    return InstanceClient.get<ScheduleSync_CoreEventRef[]>(url)
        .then(res => {
            dispatch({
                type: ScheduleSync_UserActionType.SCHEDULESYNC_USER_SCHEDULE,
                events: { code: StatusCode.COMPLETE, data: res.data, key, filter }
            })
        })
        .catch(err =>
            dispatch({
                type: ScheduleSync_UserActionType.SCHEDULESYNC_USER_SCHEDULE,
                events: { code: StatusCode.ERROR, error: err, filter }
            })
        );
};

export const UserTrackings = (id: string) => (dispatch: Dispatch<ScheduleSync_UserTrackingsAction>) => {
    const storeKey = store.getState().schedulesync_users.userTrackings?.key;
    const key = id;
    if (key === storeKey) {
        return;
    }

    dispatch({
        type: ScheduleSync_UserActionType.SCHEDULESYNC_USER_TRACKINGS,
        userTrackings: { code: StatusCode.PENDING },
    });
    return InstanceClient.get<ScheduleSync_CoreTrackings>(`/api/schedulesync/v1/core/user/${id}/trackings`)
        .then(res => {
            dispatch({
                type: ScheduleSync_UserActionType.SCHEDULESYNC_USER_TRACKINGS,
                userTrackings: { code: StatusCode.COMPLETE, data: res.data, key: key }
            })
        })
        .catch(err =>
            dispatch({
                type: ScheduleSync_UserActionType.SCHEDULESYNC_USER_TRACKINGS,
                userTrackings: { code: StatusCode.ERROR, error: err }
            })
        );
};

const initialState: ScheduleSyncUserState = {
    users: { code: StatusCode.PENDING },
    events: { code: StatusCode.NONE, filter: {} },
    userTrackings: { code: StatusCode.NONE },
    filter: {},
    user: { code: StatusCode.NONE },
}

export const reducer: Reducer<ScheduleSyncUserState, ScheduleSync_UserAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case ScheduleSync_UserActionType.SCHEDULESYNC_USERS:
            return {
                ...state,
                users: action.users,
            }
        case ScheduleSync_UserActionType.SCHEDULESYNC_USERS_FILTER:
            return {
                ...state,
                filter: action.filter,
            };
        case ScheduleSync_UserActionType.SCHEDULESYNC_USERS_UPDATE:
            return {
                ...state,
                users: { ...state.users, code: StatusCode.PENDING },
            };
        case ScheduleSync_UserActionType.SCHEDULESYNC_USER:
            return {
                ...state,
                user: action.user,
            }
        case ScheduleSync_UserActionType.SCHEDULESYNC_USER_TRACKINGS:
            return {
                ...state,
                userTrackings: action.userTrackings,
            };
        case ScheduleSync_UserActionType.SCHEDULESYNC_USER_SCHEDULE:
            return {
                ...state,
                events: action.events,
            }
        default:
            return state;
    }
};
